/**
 * Taking an order while somebody is talking.
 *
 * The whole screen is built around one fact: a dispatcher is on the phone, typing, and
 * cannot look at the keyboard or reach for a mouse. Everything on the common path is
 * reachable with Tab, Enter, and the arrow keys, and the form never moves under them.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import type { CallPrefill } from '../calls/CallPop';
import type { PlaceView } from '../places/api';
import { usePlacesFeatures } from '../places/features';
import { LockDialog } from '../places/LockDialog';
import { useEntranceWay } from '../shell/entranceWay';
import { useNumberWay } from '../shell/numberWay';
import { usePreferences } from '../shell/preferencesContext';
import { Banner, Button, Card, CardHeader, Chip, ChipGroup, cx, Field, Hint, Icon, Input, Segmented } from '../ui';
import { vehicleChoicesApi, type VehicleChoices } from '../vehicles/api';
import { FeatureChips } from '../vehicles/FeatureChips';
import { offeredFeatures } from '../vehicles/words';
import { usePreBookingsEnabled } from './preBookingsSetting';
import { AddressField } from './AddressField';
import { OrderKeys } from './OrderKeys';
import { drawnLevels, placesOf, resolve, scopeName, scopePositions, type Place } from './addresses';
import {
  customersApi,
  ordersApi,
  type AddressSuggestion,
  type Customer,
  type DrawnLevels,
  type OrderDetail,
  type SearchMode,
} from './api';

export function OrderEntry({
  onCreated,
  prefill,
  lastTaken,
  compact,
  className,
}: {
  onCreated: (order: OrderDetail) => void;

  /**
   * A call the dispatcher has just taken.
   *
   * The screen-pop hands over the number, the name and the pickup rather than the form
   * fetching them again: everything here already arrived with the ringing event, and the
   * seconds this saves are the only ones that matter.
   */
  prefill?: CallPrefill | null;

  /**
   * The reference of the last order taken, shown as the card's footer.
   *
   * It stays until the next order replaces it rather than flashing past: a dispatcher
   * looking at the telephone rather than the screen still needs the reference.
   */
  lastTaken?: string | null;

  /** The map view's narrower arrangement: notes beside For later, one line saved. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  // Initialised from the call rather than written into the form afterwards. The shell gives
  // this component a key that changes with the call, so taking one resets the form to the
  // caller instead of leaving the previous order's fields underneath.
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  /**
   * Whether the number is a box at the moment. It is a line of text the rest of the time:
   * the number comes with the call, and the pencil at the end of the line is for the rare
   * one typed by hand — a caller who rang from somebody else's telephone, a walk-in.
   */
  const [editingPhone, setEditingPhone] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [name, setName] = useState(prefill?.name ?? '');
  const [saved, setSaved] = useState<AddressSuggestion[]>([]);

  const [pickupText, setPickupText] = useState(prefill?.pickup?.address ?? '');
  const [pickup, setPickup] = useState<Place | null>(
    prefill?.pickup == null
      ? null
      : {
          address: prefill.pickup.address,
          longitude: prefill.pickup.longitude,
          latitude: prefill.pickup.latitude,
        },
  );

  /**
   * The caller's addresses, as chips under the caller's name.
   *
   * From the call first, so they are on screen before the lookup below has run: the pop
   * carried them, and the seconds between answering and the caller's first sentence are
   * the whole point. The lookup's answer replaces them once it lands — the same list,
   * ordered by use — so a number typed by hand gets the chips too. Six is enough for the
   * usual doorways; the field's own list has the rest. Held as state rather than read
   * from the call, because the form outlives the order it takes: once it is cleared, the
   * caller's doorways must go with it.
   */
  const [fromCall, setFromCall] = useState<Place[]>(placesOf(prefill?.addresses ?? []));
  const choices: Place[] = (saved.length > 0 ? placesOf(saved) : fromCall).slice(0, 6);

  const [destinationText, setDestinationText] = useState('');
  const [destination, setDestination] = useState<Place | null>(null);

  /**
   * How far the address boxes search (#382): one switch for the order, above the pickup, in
   * town at the start of every order. One rather than one per box, because the card is a
   * narrow column and a switch on each label crowded both; flipping it for the airport
   * after the pickup is placed changes nothing about the pickup.
   */
  const [levels, setLevels] = useState<DrawnLevels | null>(null);
  const [scope, setScope] = useState<SearchMode>('town');
  const positions = scopePositions(levels);
  const [notes, setNotes] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** What the car must be and offer (#369): empty for nearly every call, which takes any car. */
  const [carChoices, setCarChoices] = useState<VehicleChoices | null>(null);
  const [features, setFeatures] = useState<string[]>([]);

  const pickupRef = useRef<HTMLInputElement>(null);

  // The cursor starts in the pickup — on the first paint and again when a call remounts the
  // form — because that is the first thing typed; the number is already on its line.
  useEffect(() => {
    pickupRef.current?.focus();
  }, []);

  /** Whether the firm takes orders for later: off, the form has no "for later" box. */
  const preBookings = usePreBookingsEnabled();

  /**
   * The firm's own addresses (#420): how the boxes offer a building's entrances, and whether
   * this person may place the pickup's door — an administrator always, a dispatcher where the
   * firm lets them. The server refuses either way when they may not.
   */
  const entranceWay = useEntranceWay();
  const numberWay = useNumberWay();
  const { access } = usePreferences();
  const placesFeatures = usePlacesFeatures();
  const mayPlace = access.settings || placesFeatures.dispatchersPlace;
  const [locking, setLocking] = useState(false);

  useEffect(() => {
    let current = true;

    // A firm whose list cannot be read gets no row rather than no form: the order is
    // still taken, for any car.
    vehicleChoicesApi.get().then(
      (answer) => {
        if (current) setCarChoices(answer);
      },
      () => undefined,
    );

    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;

    void drawnLevels().then((answer) => {
      if (current) setLevels(answer);
    });

    return () => {
      current = false;
    };
  }, []);

  /**
   * Looks the caller up as soon as the number is long enough.
   *
   * Debounced, because a dispatcher types a number in one burst and a request per keystroke
   * would be seven lookups to answer one question.
   */
  useEffect(() => {
    const digits = phone.replace(/\D/g, '');

    const timer = setTimeout(() => {
      void (async () => {
        // The reset for a too-short number runs on the timer too. Clearing state
        // synchronously in the effect body would start a second render before this one had
        // committed, and nothing about a half-typed number is urgent.
        if (digits.length < 6) {
          setCustomer(null);
          setSaved([]);
          return;
        }

        try {
          const found = await customersApi.byPhone(phone);
          setCustomer(found ?? null);

          if (found) {
            // The reason the telephony integration is worth building: their usual
            // destinations are on screen before they have finished saying hello.
            setName((current) => current || (found.displayName ?? ''));
            setSaved(await customersApi.addresses(found.id));
          } else {
            setSaved([]);
          }
        } catch {
          // A failed lookup must not block taking the order. The dispatcher can still type
          // an address, and a caller waiting on the line matters more.
          setCustomer(null);
          setSaved([]);
        }
      })();
    }, 350);

    return () => clearTimeout(timer);
  }, [phone]);

  /**
   * Back to an empty form, with the cursor in the pickup address: after an order, and on
   * Escape anywhere in the form.
   *
   * The cursor waits where every order has to start: the pickup is the one field the form
   * cannot be taken without, and the number is not typed — it comes with the call, which
   * does not come through here at all but remounts the form with the number filled in.
   */
  function clear() {
    setPhone('');
    setEditingPhone(false);
    setName('');
    setPickupText('');
    setPickup(null);
    setDestinationText('');
    setDestination(null);
    setScope('town');
    setNotes('');
    setScheduledFor('');
    setFeatures([]);
    setCustomer(null);
    setSaved([]);
    setFromCall([]);
    setError(null);
    pickupRef.current?.focus();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    setBusy(true);
    setError(null);

    try {
      // Resolved here rather than demanded up front. A dispatcher types an address the
      // gazetteer has never seen — a new building, a landmark only locals use — and refusing
      // the order because they did not pick from a list would break the case this business
      // runs on. Whatever is in the box gets geocoded now.
      const from = pickup ?? (await resolve(pickupText, customer?.id ?? null, scope));

      if (from === null) {
        setError(t('order.pickupRequired'));
        return;
      }

      // The destination is optional, but if one was typed it needs coordinates: the API
      // refuses an address it cannot place rather than silently dropping it.
      const to =
        destinationText.trim().length === 0
          ? null
          : (destination ?? (await resolve(destinationText, customer?.id ?? null, scope)));

      if (destinationText.trim().length > 0 && to === null) {
        setError(t('order.destinationNotFound'));
        return;
      }

      const order = await ordersApi.create({
        customerPhone: phone.trim() || null,
        // The name the firm already has for this number, or the one the screen-pop brought;
        // the form no longer asks for one (a dispatcher on the phone does not type it).
        customerName: name.trim() || null,
        pickupAddress: from.address,
        pickupLongitude: from.longitude,
        pickupLatitude: from.latitude,
        pickupNotes: notes.trim() || null,
        pickupPlaceId: from.placeId ?? null,
        destinationAddress: to?.address ?? null,
        destinationLongitude: to?.longitude ?? null,
        destinationLatitude: to?.latitude ?? null,

        // A datetime-local input has no timezone, so it is read as the dispatcher's own wall
        // clock — which is the clock they and the caller are both talking about.
        scheduledFor:
          !preBookings || scheduledFor === '' ? null : new Date(scheduledFor).toISOString(),

        // So the server can clear the call from the missed-call queue in the same request,
        // rather than trusting this client to remember a second one.
        fromCallId: prefill?.callId ?? null,

        vehicleFeatures: features,
      });

      onCreated(order);

      // Cleared and refocused so the next call can start immediately. A dispatcher on a busy
      // evening takes these back to back.
      clear();
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), {
              ...failure.problem.arguments,
              defaultValue: t('errors.unknown'),
            })
          : t('errors.unknown'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={className}>
      <form
        onSubmit={submit}
        // The question mark promises it: Enter takes the order, Escape clears the form. An address
        // list that is open eats the first Escape itself, so a wrong suggestion is dismissed
        // without the number going with it.
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          clear();
        }}
      >
        <CardHeader
          title={t('order.newOrder')}
          actions={
            // Every key the form answers to, for whoever wants the call taken without the
            // mouse — on request, rather than a line of shortcuts in the header all day.
            <Hint label={t('order.keysHint')} align="end" wide>
              <OrderKeys
                scopes={positions.map((position) => scopeName(t, position, levels))}
                entrances={entranceWay}
              />
            </Hint>
          }
        />

        <div
          className={cx(
            'grid grid-cols-2 gap-x-3 gap-y-2.5 px-3.5',
            compact ? 'pt-2.5 pb-3' : 'pt-3 pb-3.5',
          )}
        >
          {/* The number on one line with its label, as it arrives with the call, rather than a
              box nobody types into. The pencil opens the box in its place for the rare number
              entered by hand; leaving the box closes it again. Out of the Tab order, like the
              scope switch: the form is filled by tabbing from box to box. */}
          <div className="col-span-2 flex items-center gap-2 h-8">
            <span className="text-secondary font-medium text-ink-2 shrink-0">{t('order.phone')}</span>
            {editingPhone ? (
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setEditingPhone(false)}
                aria-label={t('order.phone')}
                inputMode="tel"
                autoComplete="off"
                autoFocus
                className="flex-1 min-w-0"
              />
            ) : (
              <>
                <span
                  className={cx(
                    'flex-1 min-w-0 truncate text-body font-semibold tabular-nums',
                    phone ? 'text-ink' : 'text-ink-3 font-normal',
                  )}
                >
                  {phone || t('order.noPhone')}
                </span>
                <Button
                  variant="quiet"
                  size="sm"
                  icon
                  tabIndex={-1}
                  title={t('order.editPhone')}
                  aria-label={t('order.editPhone')}
                  onClick={() => setEditingPhone(true)}
                >
                  <Icon name="pencil" small />
                </Button>
              </>
            )}
          </div>

          {/* Announced, because a dispatcher who is listening rather than looking still
              needs to know the caller was recognised. */}
          {customer !== null && (
            <Banner tone={customer.isBlocked ? 'danger' : 'ok'} className="col-span-2">
              {customer.isBlocked
                ? t('order.blocked')
                : customer.displayName
                  ? t('order.knownNamed', { name: customer.displayName, count: customer.rideCount })
                  : t('order.known', { count: customer.rideCount })}
            </Banner>
          )}

          {/* The caller's own doorways, one click each, where the eye already is: the
              pickup is filled with the usual one and this is how the others are reached
              without clearing the box. A row of chips rather than anything that opens or
              moves — the dispatcher is listening, and the form must stay where it was. */}
          {choices.length > 0 && (
            <ChipGroup label={t('order.savedAddresses')} className="col-span-2">
              {choices.map((place) => (
                <Chip
                  key={place.address}
                  current={pickup?.address === place.address}
                  title={place.label ?? undefined}
                  onClick={() => {
                    setPickupText(place.address);
                    setPickup(place);
                  }}
                >
                  {place.label ?? place.address}
                </Chip>
              ))}
            </ChipGroup>
          )}

          {/* Where the address boxes search. Its own row, always in the same place once the
              firm has drawn its town, so it never moves the form. Out of the Tab order: the
              form is filled by tabbing from box to box, and Alt with the position's number
              moves it from inside either box. */}
          {positions.length > 0 && (
            <div className="col-span-2 flex items-center justify-between gap-2">
              <span className="text-secondary font-medium text-ink-2">{t('order.scope.label')}</span>
              <Segmented
                label={t('order.scope.label')}
                value={scope}
                tabbable={false}
                compact
                options={positions.map((position, index) => ({
                  value: position,
                  label: scopeName(t, position, levels),
                  title:
                    position === 'auto'
                      ? `${t('order.scope.autoAbout')} · ${t('order.scope.shortcut', { key: index + 1 })}`
                      : t('order.scope.shortcut', { key: index + 1 }),
                }))}
                onChange={setScope}
              />
            </div>
          )}

          <AddressField
            ref={pickupRef}
            className="col-span-2"
            id="pickup"
            label={t('order.pickup')}
            text={pickupText}
            onText={setPickupText}
            onChosen={setPickup}
            chosen={pickup}
            customerId={customer?.id ?? null}
            saved={saved}
            required
            levels={levels}
            scope={scope}
            onScope={setScope}
            entrances={entranceWay}
            numbers={numberWay}
            labelAction={
              // Placing the door from the order, once there is an address to place (#420).
              mayPlace && pickupText.trim().length > 0 ? (
                <button
                  type="button"
                  tabIndex={-1}
                  className="text-secondary text-brand hover:underline"
                  title={t('places.lock.about')}
                  onClick={() => setLocking(true)}
                >
                  {pickup?.placeId != null ? t('places.lock.move') : t('places.lock.open')}
                </button>
              ) : undefined
            }
          />

          <LockDialog
            open={locking}
            pickup={pickup}
            text={pickupText}
            onClose={() => setLocking(false)}
            onPlaced={(place) => {
              const taken = fromPlace(place);

              setPickupText(taken.address);
              setPickup(taken);
            }}
          />

          {/* The destination gets the same lookup as the pickup. It used to be a plain text
              box, which meant an address with no coordinates — and the API refuses one of
              those rather than silently losing where the passenger asked to go. */}
          <AddressField
            className="col-span-2"
            id="destination"
            label={t('order.destination')}
            text={destinationText}
            onText={setDestinationText}
            onChosen={setDestination}
            chosen={destination}
            customerId={customer?.id ?? null}
            saved={saved}
            levels={levels}
            scope={scope}
            onScope={setScope}
            entrances={entranceWay}
            numbers={numberWay}
          />

          {/* What the caller needs the car to have, as labels — the way a passenger app asks:
              suitcases for a big car, a pet small or big, a card terminal, a stroller, a
              wheelchair. None for nearly every call, which takes the nearest car. Above the
              notes, so what dispatch matches on is read before what the driver is told. */}
          {carChoices !== null && offeredFeatures(carChoices.features, features).length > 0 && (
            <Field label={t('vehicles.featuresLabel')} className="col-span-2">
              <FeatureChips
                short
                enterSubmits
                features={carChoices.features}
                chosen={features}
                label={t('vehicles.featuresLabel')}
                onChange={setFeatures}
              />
            </Field>
          )}

          <Field label={t('order.notes')} className="col-span-2">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('order.notesPlaceholder')}
              autoComplete="off"
            />
          </Field>

          {/* Empty for the overwhelming majority of calls, which are for now. Left as a
              plain optional field rather than behind a toggle: a dispatcher taking a booking
              for tomorrow should not have to find a switch first while somebody is talking. */}
          {/* Gone at a firm that takes no orders for later; the button keeps its corner. */}
          {preBookings ? (
            <Field label={t('order.scheduledFor')}>
              <Input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </Field>
          ) : (
            <span />
          )}

          <div className="flex items-end justify-end">
            <Button type="submit" variant="primary" disabled={busy || customer?.isBlocked === true}>
              {busy ? t('order.taking') : t('order.take')}
            </Button>
          </div>

          {error !== null && (
            <Banner tone="danger" className="col-span-2">
              {error}
            </Banner>
          )}
        </div>

        {/* The confirmation of the last order taken is the card's footer, so a dispatcher
            looking at the telephone rather than the screen finds the reference where they
            left it. It stays until the next order replaces it. */}
        {lastTaken != null && (
          <Banner tone="ok" footer>
            {t('order.taken', { reference: lastTaken })}
          </Banner>
        )}
      </form>
    </Card>
  );
}

/** A place just saved, as the pickup the order form takes: where the car stops, and the door. */
function fromPlace(place: PlaceView): Place {
  const stop = place.stop ?? place.door;

  return {
    address: place.line,
    longitude: stop.longitude,
    latitude: stop.latitude,
    placeId: place.id,
    firm: {
      id: place.id,
      building: place.building,
      entrance: place.entrance ?? null,
      waiting: place.state === 'waiting',
      reported: place.reported,
      name: place.names[0] ?? null,
      note: place.note ?? null,
      door: place.stop == null ? null : place.door,
    },
  };
}
